/**
 * JavaScript for the donate page.
 *
 * Due to Google reCaptcha integration, this object is created manually inside this file (rather than being created by the loader)
 * and the instance exposed on the 'window' object. It needs to be accessible globally in the reCaptcha callback function.
 *
 * There are three different Stripe integrations used on this page, each one has a slightly different flow through
 * the code.
 * 1. Card Element, this is used for card single/repeat donations
 * 2. Payment Element, this is used for BACS direct debit repeat donations and Pay by Bank single donations
 * 3. Express Checkout Element, this is used for single PayPal/Google Pay/Apple pay donations
 */

// todo: Instead of deriving the payment method from various things, just set it when the relevant button
//       is interacted with - much simpler.

'use strict';

const AbandonedDonationNotificationManager = require('Scripts/donate-form/abandoned-donation-notification-manager').default;
const AnalyticsEvent = require('Scripts/common/analytics-event').default;
const AutoPopulateAddress = require('Scripts/donate-form/auto-populate-address').default;
const bsCollapse = require('Scripts/common/bs-collapse');
const DonationBoosts = require('Scripts/donate-form/donation-boosts').default;
const FeesSlider = require('Scripts/donate-form/fees-slider').default;
const getUserLocale = require('Scripts/common/get-user-locale');
const showErrorBar = require('Scripts/common/show-error-bar').default;
const stripeStyles = require('Scripts/donate-form/stripe-styles');
const StripeExpressElements = require('Scripts/donate-form/stripe-express-elements').default;
const BacsDebitPaymentDetailsCollection = require('Scripts/donate-form/bacs-debit-payment-details-collection').default;
const StripeUtils = require('Scripts/donate-form/stripe-utils');
const PayByBank = require('./pay-by-bank').default;
const { 
    getDonationDataFromUrl, 
    preFillDonationForm, 
    redirectToTapToDonateAppSuccessScreen, 
    redirectToTapToDonateAppFailureScreen 
} = require('Scripts/donate-form/tap-to-donate-web-donation');
const { extractBillingDetailsFromDonateForm } = require('Scripts/donate-form/utils');
const { 
	enableTomSelects, 
	enableLoqate, 
	enablePostcodeLabelToggle 
} = require('Scripts/donate-form/address-utils');
const { 
	enableParsleyPostcodeValidator, 
	enableParsleyBlocklistValidator, 
	enableParsleyNotEmailValidator, 
	enableParsleyNotHtmlValidator,
} = require('Scripts/common/parsley-utils');

module.exports = Donate;

// Global variable, used for enabling browser's back button
var settingHash = false;

// See comment at top of file for why this is here.
if($('[data-module-group="donate"]').length > 0) {
	window.donateInstance = new Donate();
}

// Steps
const STEP_DETAILS = 'details';
const STEP_REVIEW = 'review';

// Payment method types
const PAYMENT_METHOD_CARD = 'card';
const PAYMENT_METHOD_EXPRESS_CHECKOUT = 'express_checkout';
const PAYMENT_METHOD_BACS_DEBIT = 'bacs_debit';
const PAYMENT_METHOD_PAY_BY_BANK = 'pay_by_bank';

function Donate() {
	var self = this;

    // Whether this form is loaded through the Tap to Donate app
    this.isTapToDonateWebDonation = false;

	// Constants
	this.fees = Number($("#feePercentage").val());
	this.giftaid = 0.25;

    // Donation amount including fees
    this.paymentValue = 0;

	// If lots of errors then we disable Stripe elements
	this.errorCount = 0;

	// Holds a reference to StripeExpressElements, which manages Stripe's ExpressElements
	this.expressElements = null;

	// Holds a reference to Stripe's API object
	this.stripe = null;

	// Stripe shared utilites, shared between different pages
	this.stripeUtils = null;

	// Holds a reference to Stripe's card number field
	this.cardNumberEl = null;

	// This gets set when the user interacts with the express checkout button(s) (PayPal/Apple/Google Pay)
	// It gets set back to false when the user closes the express checkout interface(s).
	this.usedExpressCheckoutButton = false;

	// Let's us know whether Google Recaptcha has loaded or not.
	this.recaptchaLoaded = false;

	// Donations over this amount require the user to verify its legitimacy
	this.verificationThreshold = parseInt($(document.body).data('verificationThreshold'), 10);

	this.init = function() {
		this.setupParsley();
		this.bindEventHandlers();
		this.initStripe();
		this.updateDonationTotal();
		this.resetUrlHash();
		this.setupAddressUtils();
		this.determineGiftAidVisiblity();

		this.boosts = new DonationBoosts({
			onBoost: amount => {
				this.ui.amountField.val(amount);
				this.ui.amountField.trigger("change");
			}
		});

		this.feesSlider = new FeesSlider({
			amountField: this.ui.amountField,
			onSetFeePercentage: feePercentage => this.setFee(feePercentage, null),
			onSetCustomAmount: feeAmount => this.setFee(null, feeAmount),
		});

		this.abandonedDonationNotification = new AbandonedDonationNotificationManager({
			type: $("#eventSeq").val() ? "page" : "charity",
			id: $("#eventSeq").val() || $("#charityId").val(),
		});

        const { donation } = getDonationDataFromUrl(window.location.href);
        if (donation) {
            this.isTapToDonateWebDonation = true;
            preFillDonationForm(donation, this);
        }
	}

	this.resetUrlHash = function() {
		var windowLocation = window.location;
		var currentStep = $('.js-step.show').data('step');

		if(windowLocation.hash != currentStep){
			history.pushState("", document.title, windowLocation.pathname + windowLocation.search + '#' + currentStep);
		}
	}

	this.selectElements = function() {
		this.ui = {
			// Containers
			steps: $('.js-step'),
			buttonNext: $('.js-next-step'),
			buttonPrev: $('.js-prev-step'),
			// Hidden fields
			feeField: $("#feePercentage"),
			feeAmountField: $("#feeAmount"),
			feeThresholds: $(".js-fee-threshold"),
			paymentMethodIdField: $('#sessionId'), // PaymentMethod ID gets saved as session_id on the DB
			paymentIntentIdField: $('#trackingId'), // PaymentIntent ID gets saved as trackingid on the DB
            customerIdField: $('#customerId'),
			giftAidInd: $('#giftaidInd'),
			recaptchaTokenField: $('#recaptchaToken'),
			recaptchaLoadedField: $('#recaptchaLoaded'),
			paymentMethodField: $('#paymentMethod'),
			// Amount screen
			amountField: $('#donation-amount'),
			toggleAmountGroup: $('.js-set-amount-group'),
			coverFeesField: $('.js-cover-fees'),
			topupAmount: $('.js-topup-amount'),
			topupAmountPreview: $('.js-topup-amount-preview'),
			typeField: $('input[name="type"]'),
			repeatOptions: $('.js-repeat-options'),
			repeatDateField: $('#repeatDate'),
			repeatDayField: $('#repeatDay'),
			repeatMonthField: $('#repeatMonth'),
			repeatDay: $('.js-repeat-day'),
			repeatMonth: $('.js-repeat-month'),
			repeatInfo: $('.js-repeat-info'),
			// Details screen
			screenName: $('#name'),
			makeAnon: $('.js-make-anon'),
			phoneContainer: $('.js-phone-container'),
			phoneInput: $('.js-phone-input'),
			msgInput: $('#message'),
			presetMsgs: $('.js-preset-messages'),
			// Gift Aid screen
			donationTotalGiftaid: $('.js-donation-total-giftaid'),
			giftAidCheckbox: $('input[name="giftAid"]'),
			giftAidRemove: $('.js-remove-giftaid'),
			giftAidStatement: $('.js-giftaid-statement'),
			giftAidAnswer: $('.js-giftaid-statement-answer'),
			giftAidAddress: $('.js-giftaid-address'),
			giftAidAddressFields: $('.js-giftaid-address input, .js-giftaid-address select'),
			giftAidContainer: $(".js-giftaid-address-fields-container"),
			// Confirmation screen
			giftAidNot: $('.js-giftaid-not'),
			giftAidAmount: $('.js-giftaid-amount'),
			// Payment method screen
			expressCheckoutContainer: $('.js-express-checkout'),
			paymentMethodStep: $('#js-step-payment-method'),
			paymentMethodSpinner: $('#js-payment-method-spinner'),
			paymentMethodDirectDebitBtn: $('.js-direct-debit-btn'),
            paymentMethodPayByBankBtn: $('.js-pay-by-bank-btn'),
			paymentMethodPayByBankContainer: $('.js-pay-by-bank-container'),
			paymentMethodCardBtn: $('.js-card-btn'),
			// Card payment screen
			copyAddress: $('.js-copy-address'),
            billingAddressContainer: $('.js-billing-address-container'),
            billingAddress1: $('input[name="billing-address-1"]'),
			billingContainer: $(".js-billing-address-toggle"),
			billingCountry: $('#billing-country'),
            billingCity: $('input[name="billing-city"]'),
            billingName: $('input[name="billing-name"]'),
            billingNameContainer: $('.js-billing-name-container'),
			billingNameLabel: $('.js-billing-name'),
            billingZip: $('input[name="billing-zip"]'),
			donationTotal: $('.js-donation-total'),
			paymentTotal: $('.js-payment-total'),
			donateButton: $('.js-donate'),
			makeDonation: $('.js-make-donation'),
			cardElementsContainer: $('.js-card-elements'),
			cardPaymentText: $('.js-card-payment-text'),
			// Thank you screen
			thankyouScreen: $('#js-thank-you'),
			declinedScreen: $('#js-declined'),
			declinedMessage: $('.js-declined-message'),
			donationReference: $('.js-donation-ref'),
			// Success messages
			defaultSuccessMsg: $('.js-default-success-msg'),
			verifySuccessMsg: $('.js-verification-success-msg'),
			forms: {
				donation: $('.js-main-donation-form')
			}
		};

		this.repeatDonationPaymentMethods = {
			'card': this.ui.paymentMethodCardBtn,
			'payment-element': this.ui.paymentMethodDirectDebitBtn
		};

		this.singleDonationPaymentMethods = {
			'card': this.ui.paymentMethodCardBtn,
			'express-checkout': this.ui.expressCheckoutContainer,
			'pay-by-bank': this.ui.paymentMethodPayByBankBtn
		};
	}

	this.setupParsley = function() {
		enableParsleyPostcodeValidator();
		enableParsleyBlocklistValidator();
		enableParsleyNotEmailValidator();
		enableParsleyNotHtmlValidator();
	}

	this.setupAddressUtils = function() {
		enableTomSelects([
			"#country", 
			"#billing-country",
		]);

		enableLoqate([
			"#giftaid-loqate",
			"#billing-loqate",
		]); // Needs to be after enableTomSelects

		enablePostcodeLabelToggle();

		new AutoPopulateAddress({
			inputSources: '.js-giftaid-address-fields-container input, .js-giftaid-address-fields-container select',
			textTarget: '.js-autopopulated-address',
			textTargetContainer: $(".js-autopopulated-address-container"),
			hideContainer: $(".js-billing-address-search"),
			stepToTriggerPopulate: $("#js-step-review"),
			prePopulateCheck: this.isGiftAidSelected,
			editButton: $('.js-manual-billing-address'),
			onComplete: () => {
				this.onBillingContainerShown();
			}
		});
	}

	this.determineGiftAidVisiblity = function() {
		if(!getUserLocale().isUkExpanded && this.ui.giftAidCheckbox.length){
			console.log("Remove Gift Aid section");
			$("#js-step-giftaid").attr("data-enabled", false); // .remove() doesn't fully work as self.ui.steps doesn't refresh for some reason
			this.ui.giftAidCheckbox.prop("disabled", true); // Remove from validation
			$(".js-giftaid-review").remove();
		}
	}

	this.bindEventHandlers = function() {
		this.selectElements();

		// Next buttons (prev button handled in enableDonationButton())
		this.ui.buttonNext.on('click', this.goToNextStep);

		// Amount screen
		this.ui.amountField.on('change keyup', this.onAmountChanged);
		this.ui.coverFeesField.on('change', this.onToggleCoverFees);
		this.ui.toggleAmountGroup.on('click', '.js-set-amount', this.toggleAmount);
		this.ui.typeField.on('change', this.onTypeChanged);
		this.ui.repeatDateField.on('change', this.onRepeatDateChanged);

		// Details screen
		this.ui.makeAnon.on('change', this.onToggleAnon);
		this.ui.msgInput.on('change keyup', this.onUpdateMsg);
		this.ui.presetMsgs.on('click', 'button', this.onClickPresetMsg);

		// Gift Aid screen
		this.ui.giftAidCheckbox.on('change', this.onToggleGiftAid);
		this.ui.giftAidAnswer.on('change', this.onGiftAidAnswer);
		this.ui.giftAidContainer.on('show.bs.collapse', this.onGiftAidContainerShown); 
		this.ui.giftAidRemove.on('click', this.onRemoveGiftAid);

		// Payment method screen
		this.ui.paymentMethodCardBtn.on('click', this.onClickPaymentMethodCardBtn.bind(this));
		this.ui.paymentMethodDirectDebitBtn.on('click', this.onClickPaymentMethodDirectDebitBtn.bind(this));
		this.ui.paymentMethodPayByBankBtn.on('click', this.onClickPaymentMethodPayByBankBtn.bind(this));

		// Payment screen
		this.ui.billingContainer.on('show.bs.collapse', this.onBillingContainerShown); 

		// Other
		this.ui.forms.donation.on('keydown', this.onKeydown);

		// Bind form validation
		this.ui.forms.donation.parsley({
			excluded: ':disabled',
			errorsContainer: function(field) {
				var $el = field.$element;
				return $el.closest('.form-group').find('.error-container');
			}
		});

		// Scroll to first form error
		self.ui.forms.donation.parsley().on('form:error', (e) => {
			var firstError = $($(".js-step.show .parsley-errors-list li")[0]);
			var closestContainer = firstError.closest(".box__content");
			$("html, body").animate({scrollTop: (closestContainer.offset() || firstError.offset() || $("main").offset()).top - 50}, "slow");
		});

		$(window).bind('hashchange', this.onHashChanged);

		$(window).on('load', function() {
			if(typeof grecaptcha !== 'undefined') {
				grecaptcha.ready(function() {
					console.log('Donate.bindEventHandlers', 'Google recaptcha has been loaded.');
					this.recaptchaLoaded = true;
					this.ui.recaptchaLoadedField.val(this.recaptchaLoaded);
				}.bind(this));
			}
		}.bind(this));
	}

    this.toggleBillingFields = function({ isVisible }) {
        // Billing name
        this.ui.billingName.attr('disabled', !isVisible);
        this.ui.billingNameContainer.toggle(isVisible);

        // Billing address
        this.ui.billingAddress1.attr('disabled', !isVisible);
        this.ui.billingCity.attr('disabled', !isVisible);
        this.ui.billingZip.attr('disabled', !isVisible);
        this.ui.billingAddressContainer.toggle(isVisible);

        // Hidden field that says if the address form has been shown (or if autocompleted via Gift Aid)
        $("#billing-address-shown").attr('disabled', !isVisible);
    }

    this.onClickPaymentMethodPayByBankBtn = function() {
        // Hide BACS debit UI
        this.bacsDebitPaymentDetailsCollection.hide();

        // Hide card payment UI
        this.ui.cardPaymentText.hide();
        this.ui.cardElementsContainer.hide();

        // Show Pay by Bank UI
        this.payByBank.activate();

        // Modifications specific to this payment method
        this.toggleBillingFields({ isVisible: false });
    }

	this.onClickPaymentMethodCardBtn = function() {
        // Hide BACS debit UI
        this.bacsDebitPaymentDetailsCollection.hide();

        // Show card payment UI
		this.ui.cardPaymentText.show();
		this.ui.cardElementsContainer.show();

        // Hide Pay by Bank UI
        this.payByBank.deactivate();

        // Modifications specific to this payment method
        this.toggleBillingFields({ isVisible: true });
		this.ui.billingNameLabel.text('Name on card');
	}

	this.onClickPaymentMethodDirectDebitBtn = function() {
        // Show BACS debit UI
        this.bacsDebitPaymentDetailsCollection.show();

        // Hide card payment UI
		this.ui.cardPaymentText.hide();
		this.ui.cardElementsContainer.hide();

        // Hide Pay by Bank UI
        this.payByBank.deactivate();

        // Modifications specific to this payment method
        this.toggleBillingFields({ isVisible: true });
		this.ui.billingNameLabel.text('Account holder name');

		// Edge case: User enters partial card details, goes back to DD screen. In all likelihood, never going to happen, but if it
		// does we don't want it to display an incorrect state.
		// This function call hides Stripe's error container.
		this.toggleStripeError({});
	}

	this.initStripe = async function() {
		console.log("Donate.initStripe");
		// stripeUtils contains the verify() method which handles all the verification interactions between the client, our server, and Stripe during the 3D secure verification process.
		self.stripeUtils = new StripeUtils({
			onSuccess: self.showThankyouScreen,
			onFail: self.showDeclinedScreen
		});

		try {
			this.stripe = await this.stripeUtils.loadStripe();
		} catch(e) {
			showErrorBar();
			this.ui.buttonNext.off("click");
			return;
		}

		// CardElement integration
		self.mountStripeCardElements();

		// Express Checkout integration
		self.expressElements = new StripeExpressElements(self);

		// Payment Element (BACS debit) integration
		this.bacsDebitPaymentDetailsCollection = new BacsDebitPaymentDetailsCollection({
            stripeJsSdk: this.stripe,
            onModifyBankDetailsClicked: () => {
                grecaptcha.reset();
                this.enableDonationButton();
            }
		});

        // Payment Element (Pay by Bank integration)
        this.payByBank = new PayByBank({
            stripeJsSdk: this.stripe,
            donationFormElement: this.ui.forms.donation[0]
        });

		self.enableDonationButton();
	}

	this.mountStripeCardElements = function() {
		console.log("Donate.mountStripeCardElements");
		var elements = self.stripe.elements({
			fonts: stripeStyles.fonts
		});

		var appearance = stripeStyles.cardElementStyles;

		var elementClasses = {
			focus: 'focus',
			empty: 'empty',
			invalid: 'parsley-error'
		};

		$('.card-error').hide();

		var cardNumber = elements.create('cardNumber', {
			placeholder: '',
			style: appearance,
			classes: elementClasses
		});

		this.cardNumberEl = cardNumber;

		cardNumber.mount('#card-number');

		cardNumber.on('change', function(event) {
			self.toggleStripeError(event)

			if(event.error) {
				self.errorCount ++;

				if(self.errorCount > 9) {
					cardNumber.unmount();
					self.disableDonationButton();
				}
			}
		});

		var cardExpiry = elements.create('cardExpiry', {
			placeholder: '',
			style: appearance,
			classes: elementClasses
		});

		cardExpiry.mount('#card-expiry');

		cardExpiry.on('change', function(event) {
			self.toggleStripeError(event)
		});

		var cardCvc = elements.create('cardCvc', {
			placeholder: '',
			style: appearance,
			classes: elementClasses
		});

		cardCvc.mount('#card-cvc');

		cardCvc.on('change', function(event) {
			self.toggleStripeError(event)
		});
	}

	this.onHashChanged = function(e) {
		// If we are setting the hash (through form buttons), we don't run this
		if(settingHash){
			settingHash = false;
			return;
		}

		var oe = e.originalEvent;
		var oldUrl = oe.oldURL;
		var newUrl = oe.newURL;

		var initialHash = self.ui.steps.first().data('step');
		var oldHash = typeof oldUrl != 'undefined' ? (oldUrl.includes("#") ? oldUrl.substring(oldUrl.indexOf('#')+1) : initialHash) : -1;
		var newHash = typeof newUrl != 'undefined' ? (newUrl.includes("#") ? newUrl.substring(newUrl.indexOf('#')+1) : initialHash) : -1;

		var oldHashPosition = self.getStepPosition(oldHash);
		var newHashPosition = self.getStepPosition(newHash);

		console.log("DonateForm.onHashChanged", "URL hash has changed from " + oldHash + " to " + newHash);

		if(newHashPosition != oldHashPosition && newHashPosition > -1) {
			var currentStep = $('.js-step.show');
			var btn = currentStep.find(newHashPosition < oldHashPosition ? self.ui.buttonPrev : self.ui.buttonNext);

			// If no buttons, we're on the success/failure screen - reload if going back as allows them to go back to start
			if(!btn.length) {
				window.location.reload();
				return;
			}

			btn.trigger('click');
			settingHash = false;
		}
	}

	this.resetRecaptcha = function() {
		console.log("Donate.resetRecaptcha");
		grecaptcha.reset();
		self.ui.recaptchaTokenField.val('');
	};

	// This gets called when the recaptcha token has been generated. Triggered by attempting a payment element/card donation
	this.onRecaptchaTokenGenerated = function(token) {
		console.log("Donate.onRecaptchaTokenGenerated", token);
		console.log('Used payment method: ', self.getUsedPaymentMethod());
		self.ui.recaptchaTokenField.val(token);
		
		// BACS debit payment
		if(self.bacsDebitPaymentDetailsCollection.hasBeenInteractedWith) {
            this.handleBacsDebitDonation();
			return;
		}

        // Bank payment
        if (self.payByBank.isActivated) {
            this.handlePayByBankDonation();
            return;
        }

		// Card payments
		self.handleCardDonation();
	};

    this.handlePayByBankDonation = async function() {
		this.ui.paymentMethodField.val(self.getUsedPaymentMethod());

        const { error } = await this.payByBank.startPayment();

        // We can't recover from errors that occur at this stage, so just prompt the user the start again.
        if (error) {
            console.log('Error startPayment', error);
            // The error is irrelevant for the user, so just show a general one.
            this.showDeclinedScreen({})
            return;
        }
    }

    this.handleBacsDebitDonation = async function() {
        const { 
            paymentMethodId, 
            customerId, 
            error,
            userClickedModifyDetails
        } = await this.bacsDebitPaymentDetailsCollection.start();

        // User amending their bank details
        if (userClickedModifyDetails) {
            return;
        }

        // Unrecoverable error
        if (error) {
            this.showDeclinedScreen(error);
            return;
        }

        // No payment details or customer were created - I've not seen this happen, but we should
        // deal with it anyway
        if (!paymentMethodId || !customerId) {
            this.showDeclinedScreen({});
            return;
        }

        console.log('Updating form fields with customer / payment method ID');
        this.ui.paymentMethodIdField.val(paymentMethodId);
        this.ui.customerIdField.val(customerId);

        console.log('Submitting payment');
        const result = await this.submitPayment();
        console.log(result);
    }

	this.handleCardDonation = function() {
		console.log("Donate.handleCardDonation");

		var billingDetails = extractBillingDetailsFromDonateForm();

		// Initial post to Stripe. This returns a paymentMethod.id token that is passed to our backend.
		self.stripe.createPaymentMethod("card", self.cardNumberEl, billingDetails)
			.then(function(result) {
				if (result.error) {
					// This is going to be an error where Stripe complains about the form inputs.
					self.toggleStripeError(result)
					self.enableDonationButton();
					self.resetRecaptcha();

					return;
				}
				self.submitPayment(result).fail(function() {
					self.enableDonationButton();
					self.resetRecaptcha();
				});
			});
	}

	this.onClickDonateButton = function(e) {
		console.log("Donate.onClickDonateButton");
		e.preventDefault();

		self.disableDonationButton();

		// Validate form input
		self.ui.forms.donation.parsley().validate();
		var isValid = self.ui.forms.donation.parsley().isValid();

		// If not valid, don't bother generating a recaptcha token as it could've expired by the time they re-submit after
		// fixing any errors.
		if(!isValid) {
			self.enableDonationButton();
			return;
		}

		if(this.bacsDebitPaymentDetailsCollection.hasBeenInteractedWith) {
			console.log('Used BACS debit payment element');
			this.bacsDebitPaymentDetailsCollection.validate().then(result => {
				console.log('Validation result', result);
				// This automatically updates Stripe's UI with errors
				if(result.error) {
					console.log('Stripe UI showing errors');
					this.enableDonationButton();
					return;
				} else {
					console.log('All valid!');
					grecaptcha.execute().then(() => {
						// this.onRecaptchaTokenGenerated() gets called after this finishes.
					}).catch(err => {
						console.error(err);
						self.enableDonationButton();
					});
				}
			});
			return;
		}

		grecaptcha.execute().then(function() {
			// this.onRecaptchaTokenGenerated() gets called after execute() finishes, which is where the Stripe token generation
			// and other bits happen. Inside donate/recaptcha.vm is where you define what happens after a token has been created.
		}).catch(function() {
			self.enableDonationButton();
		});
	}

	// This is called when Stripe complains about the card details inputs
	this.toggleStripeError = function(event) {
		console.log("Donate.toggleStripeError");
		var container = $("#stripe-error-container");
		if(event.error) {
			if(event.error.message.indexOf('You passed an empty string') > -1) {
				event.error.message = "Please complete all required fields";
			}

			container.find(".parsley-required").text(event.error.message);
			container.show();
		} 
		else {
			container.hide();
		}
	}

	// User submits the form containing their billing information
	// It returns a promise as each method that calls it needs to handle the outcome differently.
	this.submitPayment = function(result) {
		console.log("Donate.submitPayment", result);

		// todo: stripe-express-elements is doing something similar to this, maybe we can remove
		//       that so it's all in one place.
		if(result) {
			const { paymentMethod, paymentIntent } = result;
			const paymentMethodId = paymentMethod?.id ?? paymentIntent?.payment_method;
			self.ui.paymentMethodIdField.val(paymentMethodId);

			if(paymentIntent?.id) {
				self.ui.paymentIntentIdField.val(paymentIntent.id);
			}
		}

		// This field is used to determine what type of payment was made
		self.ui.paymentMethodField.val(self.getUsedPaymentMethod());

		// Initial AJAX request to create donation row in our database.
		return $.ajax({
			url: '/donate/make',
			type: 'POST',
			data: self.ui.forms.donation.serializeArray()
		}).then(function(resp) {
			console.log("Donate.submitPayment", "response", resp);
			$(".js-signup-btn").attr("href", "/join?key="+(resp.donationSignupKey || ''));
			return self.stripeUtils.verify(resp);
		});
	}

	this.goToNextStep = function(e) {
		e.preventDefault();

		var currentStep = $(e.currentTarget).parents('.js-step').data('step');
		var nextStep = self.getNextStep(currentStep);

		console.log("Donate.goToNextStep", currentStep, nextStep);

		// Validate the first set of fields
		self.ui.forms.donation.parsley().validate({
			group: 'step-' + currentStep
		});

		var isValid = self.ui.forms.donation.parsley().isValid({
			group: 'step-' + currentStep
		});

		if(isValid) {
			if(nextStep == STEP_DETAILS) {
				// Check Express Elements Checkout payment methods - PayPal is only enabled if over £5
				// This isn't included in getAvailablePaymentMethods because it can take 1-2s to init and mount
				self.expressElements.checkMountedPaymentMethods(self.ui.amountField.val());
			}

			// note(dan): I know this is hacky, but couldn't figure out a better place to put this code.
			// It needs to be called at the point in which you show the payment methods screen - you could call it when
			// the amount/type/payment element loads/express checkout loads, but I don't think that's any better. Easier
			// to have in one place.
			if(nextStep === STEP_REVIEW) {
				const availablePaymentMethods = self.getAvailablePaymentMethods();

				// If only one payment method option then we can skip the screen
				console.log("Checking number of repeat payment methods", Object.keys(availablePaymentMethods));
				self.ui.paymentMethodStep.attr("data-enabled", Object.keys(availablePaymentMethods).length > 1);

				self.showAvailablePaymentMethods();
			}

			$('#js-step-'+currentStep).collapse('hide');
			$('#js-step-'+nextStep).collapse('show');
			self.scrollToTop();

			settingHash = true;
			window.location.hash = nextStep;

		} else { // This is here as we trigger this function when the user clicks browser forward button
			settingHash = true;
			window.location.replace('#'+currentStep);
		}
	}

	this.goToPrevStep = function(e) {
		e.preventDefault();

		var currentStep = $(e.currentTarget).parents('.js-step').data('step');
		var prevStep = self.getPrevStep(currentStep);

		console.log("Donate.goToPrevStep", currentStep, prevStep);

		$('#js-step-'+currentStep).collapse('hide');
		$('#js-step-'+prevStep).collapse('show');
		self.scrollToTop();

		settingHash = true;
		window.location.hash = prevStep;
	}

	this.getEnabledSteps = function() {
		return self.ui.steps.filter(":not([data-enabled='false'])");
	}

	this.getNextStep = function(currentStep) {
		var steps = self.getEnabledSteps();
		return $(steps[self.getStepPosition(currentStep) + 1]).data('step');
	}

	this.getPrevStep = function(currentStep) {
		var steps = self.getEnabledSteps();
		return $(steps[self.getStepPosition(currentStep) - 1]).data('step');
	}

	this.getStepPosition = function(stepName) {
		var targetStep = $("#js-step-"+stepName);
		return self.getEnabledSteps().index(targetStep);
	}

	// Manually enter a donation amount
	this.onAmountChanged = function() {
		var value = $(this).val();

		self.setAmountCss(value * 100);

		self.updateDonationTotal();
	}

	// Choose a preset donation amount
	this.toggleAmount = function() {
		var value = $(this).data().value;

		self.setAmountCss($(this).data().uri);

		// Update input field + remove
		self.ui.amountField.val(value);

		// Remove any validation errors
		self.ui.amountField.parsley().reset();
		self.updateDonationTotal();
	}

	// Set the CSS classes for the amount boxes
	this.setAmountCss = function(value) {
		// Remove any active state
		self.ui.toggleAmountGroup.find('.js-set-amount').removeClass('btn-navy').addClass('btn-default');

		// Add active state
		self.ui.toggleAmountGroup.find('.js-set-amount-' + value).removeClass('btn-default').addClass('btn-navy');
	}

	// Change between single donation and monthly
	this.onTypeChanged = function(event) {
		console.log("onTypeChanged", event.target.value);

		const isSingleDonation = event.target.value === "SINGLE"

		// Change button styles
		$(self.ui.typeField).closest('.btn').removeClass('btn-navy').addClass('btn-outline-navy');
		$(this).closest('.btn').removeClass('btn-outline-navy').addClass('btn-navy');

		self.ui.repeatOptions.collapse('toggle');
		self.ui.repeatInfo.collapse('toggle');

		if(isSingleDonation) {
			var d = new Date();
			self.ui.repeatDateField.val(d.getDate());
			self.ui.repeatDateField.trigger('change');
		}

		self.ui.paymentMethodCardBtn.toggleClass("btn-green", isSingleDonation)
		self.ui.paymentMethodCardBtn.toggleClass("btn-default", !isSingleDonation)
	}

	this.onRepeatDateChanged = function(event) {
		console.log("Repeat date changed to " + this.value + " of the month");
		
		var selected  = $(this.options[this.selectedIndex]);
		var day       = selected.data('day');
		var ordinal   = selected.data('ordinal');
		var month     = selected.data('month');
		var monthName = selected.data('monthName');
		var isToday   = this.value == (new Date).getDate();

		// Update the hidden fields
		self.ui.repeatDayField.val(day);
		self.ui.repeatMonthField.val(month);

		// Change text and show/hide sections
		self.ui.repeatDay.text(ordinal);
		self.ui.repeatMonth.text(' ' + monthName);
		$('.js-repeat-schedule').text(selected.text());
		$('.js-repeat-date').text(ordinal + ' ' + monthName);
		$('.js-donated-now').toggle(isToday);
		$('.js-donated-future').toggle(!isToday);
	}

	// Toggle between first name and 'Anonymous'
	this.onToggleAnon = function(event) {
		if( $(this).is(":checked") ){
			self.ui.screenName.val('Anonymous');
			self.ui.screenName.attr('readonly', true);
		} else if (self.ui.screenName.val() == 'Anonymous') {
			self.ui.screenName.val($(this).data('default'));
			self.ui.screenName.attr('readonly', false);
		}
	}

	this.onUpdateMsg = function(event) {
		self.ui.presetMsgs.collapse(self.ui.msgInput.val().length ? 'hide' : 'show');
	}

	this.onClickPresetMsg = function(event) {
		var selectedMsg = $(event.currentTarget);
		self.ui.msgInput.val(selectedMsg.html());
		self.ui.presetMsgs.find('button').attr('aria-selected', false).removeClass('active');
		selectedMsg.attr('aria-selected', true).addClass('active');
	}

	this.isGiftAidSelected = function() {
		console.log("DonateForm.isGiftAidSelected", self.ui.giftAidCheckbox.filter(":checked").val() == "Y");
		return self.ui.giftAidCheckbox.filter(":checked").val() == "Y";
	}

    // note: event is unused, but it accesses the clicked element with 'this'...
	this.onToggleGiftAid = function(event) {
		var giftAidSelected = self.isGiftAidSelected();

		// Enable/disable GiftAid statements
		$(self.ui.giftAidAnswer).each(function() {
			$(this).prop("disabled", !giftAidSelected).prop("required", giftAidSelected).prop('checked', false);
		});

		// Show statement
		self.ui.giftAidStatement.collapse(giftAidSelected ? 'show' : 'hide');

		// Hide address if it's already been shown
		if(!giftAidSelected) {
			self.ui.giftAidAddress.collapse('hide');
			self.ui.giftAidAddressFields.prop('disabled',true).prop('required',false);
			self.ui.giftAidInd.val(false);
		}

		// Update review page
		self.ui.giftAidNot.html(giftAidSelected ? '' : 'not ')

	}

	this.onRemoveGiftAid = function(event) {
		console.log("Donate.onRemoveGiftAid");
		$("#giftAidN").prop("checked", true).trigger("change");
	}

	this.onGiftAidAnswer = function(event) {
		var unconfirmedAnswers = $(self.ui.giftAidAnswer).not(":checked").length;
		
		// If all answers have been confirmed, show the address fields, otherwise hide them
		if(unconfirmedAnswers == 0){	
			$(self.ui.giftAidAddressFields).each(function() {
				$(this).prop("disabled", false).prop("required", !$(this).hasClass('js-not-required'));
			});

			// Show address
			self.ui.giftAidAddress.collapse('show');

			// Set the Gift Aid hidden input to true
			self.ui.giftAidInd.val(true);
		} else {
			self.ui.giftAidAddress.collapse('hide');
			self.ui.giftAidAddressFields.prop('disabled',true).prop('required',false);
			self.ui.giftAidInd.val(false);
		}
	}

	this.onGiftAidContainerShown = function() {
		self.ui.forms.donation.parsley().reset();
		$("#giftaid-address-shown").val("true");
	};

	this.onBillingContainerShown = function() {
		self.ui.forms.donation.parsley().reset();

		// Hidden field that says if the address form has been shown (or if autocompleted via Gift Aid)
		$("#billing-address-shown").val("true");
	};

	// If Stripe approve the payment
	this.showThankyouScreen = function(response) {
		self.ui.steps.collapse('hide');
		self.ui.thankyouScreen.collapse('show');
		settingHash = true
		window.location.hash = 'success';

		// Show a slightly different success messages if the user has to verify their large donation.
		let amount = parseFloat(self.ui.amountField.val()) || 0;
		if(amount >= self.verificationThreshold) {
			self.ui.defaultSuccessMsg.hide();
			self.ui.verifySuccessMsg.show();
		}

		self.scrollToTop();

		// Analytics
		AnalyticsEvent.facebook('Donate');
		AnalyticsEvent.heap('Donation', {donationValue:amount});
		AnalyticsEvent.gtm('DonationSuccess', {donationValue:amount});

		this.abandonedDonationNotification?.deleteCookie();

        if (self.isTapToDonateWebDonation) {
            redirectToTapToDonateAppSuccessScreen();
        }
        
	};

	// Response can be a few things:
	// - Our API response (DonationAjaxResponse)
	// - Stripe's Error object: https://docs.stripe.com/api/errors
	// - A native JS error
	this.showDeclinedScreen = function(response) {
		console.log('Donate.showDeclinedSecreen', response);

		// We only want to surface human-readable messages, not native JS error messages, so we need to filter
		// those out.
		let errorMsg = "There was a problem making the donation."

		if(response.stripeErrorMsg) {
			// Our API response
			errorMsg = response.stripeErrorMsg;
		} else if(response.type && response.message) {
			// Stripe Error object (native JS objects have a 'message' property, so we can't blindly use response.message)
			errorMsg = response.message;
		}

		self.ui.declinedMessage.text(errorMsg);

		// If there is a donation id, add it to the error message.
		if(response.donationId) {
			var donationRefString = self.ui.donationReference;
			var prefix = $(donationRefString).data('prefix') || '';
			donationRefString.text(prefix + response.donationId);
		}

		// Present the error message
		self.ui.steps.collapse('hide');
		self.ui.declinedScreen.collapse('show');
		settingHash = true
		window.location.hash = 'failed';
		self.scrollToTop();

		// Analytics
		AnalyticsEvent.gtm('DonationFailure', {conversionValue:self.ui.amountField.val()});
		AnalyticsEvent.heap('DonationFailure', {donationValue:self.ui.amountField.val()});

        if (self.isTapToDonateWebDonation) {
            redirectToTapToDonateAppFailureScreen();
        }
	}

	this.scrollToTop = function() {
		if($("body").hasClass("disable-animations")) return;

		const mainElement = $('main');
		const relativePosition = mainElement[0].getBoundingClientRect();

		// Don't scroll to top if already close to top (top 33% of screen)
		if(relativePosition.top > 0 && relativePosition.top < ($(window).height() / 3)) return;

		$("html, body").animate({scrollTop: mainElement.offset().top}, "slow");
	}

	// Pass in either a % or a fixed amount, then use donation amount to work out the other one
	this.setFee = function(feePercentage, feeAmount) {
		const donationValue = this.ui.amountField.val() || 0;
		feePercentage = feePercentage || (feeAmount / donationValue).toFixed(4);
		feeAmount = feeAmount || (feePercentage * donationValue).toFixed(2);

		console.log("DonateForm.setFee", feePercentage, feeAmount);

		this.fees = feePercentage;
		this.ui.feeField.val(feePercentage);
		this.ui.feeAmountField.val(feeAmount);

		// If amount not set yet, feeAmount will always be 0, so coverFeesField could be incorrectly unchecked
		if(donationValue > 0) {
			this.ui.coverFeesField.prop("checked", feeAmount > 0).trigger("change");
		}
	}

	this.calculateFeePercentage = function(donationValue) {
		console.log("DonateForm.calculateFeePercentage", donationValue);

		const feeField = this.ui.feeField;
		const feeText = $(".js-fee-perc");

		// Start with the default fee, if provided
		this.fees = Number(feeField.data('default') || feeField.val());

		// Check if above the fee thresholds, if so update the fee
		let activeThreshold = 0;
		this.ui.feeThresholds.each((i, el) => {
			const threshold = Number(el.dataset.threshold);

			if(donationValue >= threshold && threshold > activeThreshold) {
				activeThreshold = threshold;
				this.fees = Number(el.value);
			}
		});

		const feeFormatted = this.fees.toLocaleString('en-GB', {
			style: 'percent', 
			minimumFractionDigits:0, 
			maximumFractionDigits:2
		});

		feeField.val(this.fees);
		feeText.html(feeFormatted);

		console.log("DonateForm.calculateFeePercentage", "Fee set to " + feeFormatted);
	}

	this.onToggleCoverFees = function () {
		const isCovered = self.ui.coverFeesField.is(':checked');
		console.log("DonateForm.onToggleCoverFees", isCovered);
		bsCollapse(".fee-prompt").toggle(!isCovered);
		self.updateDonationTotal();
	}

	// Update the various on-screen amounts, plus the final payment value passed to Stripe
	this.updateDonationTotal = function() {
		var donationValue = parseFloat(self.ui.amountField.val()) || 0;

		// Fee percentage may vary depending on amount
		self.calculateFeePercentage(donationValue);

		// Payment value might have fees added which is separate to the donation
		var paymentValue = donationValue;

		// Update the normal total
		self.updateAmountText(self.ui.donationTotal, donationValue);

		// Update the top-up total and calculate the payment total
		if(self.ui.coverFeesField.is(':checked')){
			self.updateAmountText(self.ui.topupAmount, donationValue * self.fees);
			paymentValue = donationValue * (1 + self.fees);
		}
		else {
			self.updateAmountText(self.ui.topupAmount,0);
		}

		// Set the payment amount on the ExpressElements (needs to be a whole number in pence), if we don't do this, the donation amount stays at what it was set to originally.
		if(self.expressElements) {
			var amountInPence = parseInt((paymentValue * 100).toFixed(0));
			self.expressElements.updateAmount(amountInPence)
		}

		// Don't re-run changes, send events etc if the amount hasn't actually changed (also prevents potential loops)
		if(this.paymentValue == paymentValue) {
			return;
		}

        // Make the total donation amount available through code, instead of having to manually fetch it from the webpage
        this.paymentValue = paymentValue;

		// This top-up amount is always shown regardless of fees being covered
		self.updateAmountText(self.ui.topupAmountPreview, donationValue * self.fees);

		// Update payment total
		self.updateAmountText(self.ui.paymentTotal, paymentValue);

		// Update the total with giftaid
		self.updateAmountText(self.ui.donationTotalGiftaid, donationValue * (1 + self.giftaid));

		// Update the giftaid amount
		self.updateAmountText(self.ui.giftAidAmount, donationValue * self.giftaid);

		// Show the phone input + make it required if this donation is over the threshold, we may need to call them to verify
		if(donationValue >= self.verificationThreshold) {
			self.ui.phoneContainer.show();
			self.ui.phoneInput.prop('required', true);
		} else {
			self.ui.phoneContainer.hide();
			self.ui.phoneInput.prop('required', false);
		}

		// Run donation boosts - might not have been initiated yet hence the `?.`
		this.boosts?.checkBoosts(donationValue);

		if(this.ui.amountField[0].checkValidity()) {
			this.abandonedDonationNotification?.createCookie(donationValue);
		}

		// Refresh fee slider - might not have been initiated yet hence the `?.`
		this.feesSlider?.refresh();

		// Move Pay by Bank button to first if it is £500+
		self.ui.paymentMethodPayByBankContainer.toggleClass("order-first", donationValue >= 500);

		// Analytics
		if(donationValue > 0) {
			AnalyticsEvent.heap('DonationAmountSet', {donationValue:donationValue});
		}
	}

	// Update an amount in the HTML. Includes data-prefix and data-suffix which helps when the amount may be shown or hidden
	this.updateAmountText = function(elements, value){
		$(elements).each(function() { 
			var prefix = $(this).data('prefix') || '';
			var suffix = $(this).data('suffix') || '';

			var strOptions = {
				style: 'currency',
				currency: 'GBP',
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			};

			if(value > 0) { 
				$(this).html(prefix + value.toLocaleString('en-GB', strOptions) + suffix);
			}
			else {
				$(this).html('');
			}
		});
	}

	// Stop premature form submissions with enter but still allow enter inside textareas
	this.onKeydown = function(event) {
		var target = $(event.target);
		var keyCode = event.keyCode || event.which;

		if(keyCode === 13 && !$(document.activeElement).is('textarea')) {
			event.preventDefault();
		}
	}

	this.enableDonationButton = function() {
		console.log("Donate.enableDonationButton");
		this.ui.makeDonation.removeClass('disabled');
		this.ui.buttonPrev.removeClass('disabled');
		// Bind both form submit and button press to the click handler. This ensures form submits via
		// e.g. enter key presses are handled appropriately, rather than the default action, which is to do a POST page load
		this.ui.forms.donation.on("submit", this.onClickDonateButton.bind(self));
		this.ui.makeDonation.on("click", this.onClickDonateButton.bind(self));
		this.ui.buttonPrev.on('click', this.goToPrevStep);
	};

	this.disableDonationButton = function() {
		console.log("Donate.disableDonationButton");
		this.ui.makeDonation.addClass('disabled');
		this.ui.forms.donation.off("submit");
		this.ui.makeDonation.off("click");
		this.ui.buttonPrev.addClass('disabled');
		this.ui.buttonPrev.off("click");
	};

	// Simple helper to determine what payment method the user used based on the things they interacted with
	this.getUsedPaymentMethod = function() {
		console.log("Donate.getUsedPaymentMethod");

		if(self.usedExpressCheckoutButton) {
			return PAYMENT_METHOD_EXPRESS_CHECKOUT;
		}

		if(self.bacsDebitPaymentDetailsCollection.hasBeenInteractedWith) {
			return PAYMENT_METHOD_BACS_DEBIT;
		}
        
        if (self.payByBank.isActivated) {
            return PAYMENT_METHOD_PAY_BY_BANK;
        }

		return PAYMENT_METHOD_CARD;
	};

	// Returns a map of payment method name => container of available payment methods
	this.getAvailablePaymentMethods = () => {
		console.log('Donate.getAvailablePaymentMethods');

		const type = $('input[name="type"]:checked').val();

        // Make sure wallet payment methods + PayPal donations are disabled as they probably won't work
        // in a browser embedded inside an app.
        if (this.isTapToDonateWebDonation) {
            console.log('Tap to Donate web donation - only card is supported.');
            const singleDonationPaymentMethods = { ...this.singleDonationPaymentMethods };
            delete singleDonationPaymentMethods['express-checkout'];
            return singleDonationPaymentMethods;
        }

		if(type === 'SINGLE') {
			const singleDonationPaymentMethods = { ...this.singleDonationPaymentMethods };

			// Only show Express Checkout element if it loaded
			if(!this.expressElements?.mounted) {
				console.log('Stripe Express Checkout element not mounted, express-checkout no longer a payment method.');
				delete singleDonationPaymentMethods['express-checkout'];
			}

            if(!this.payByBank?.mounted) {
                console.log('Stripe Payment Element not mounted, Pay by Bank no longer a payment method');
                delete singleDonationPaymentMethods['pay-by-bank'];
            }

            if(this.paymentValue > 5000) {
                console.log('Amount over Pay by Bank payment limit, Pay by Bank no longer a payment method');
                delete singleDonationPaymentMethods['pay-by-bank'];
            }
			
			return singleDonationPaymentMethods;
		}

		const repeatDonationPaymentMethods = { ...this.repeatDonationPaymentMethods };

        // Don't allow direct debits for amounts over the verification threshold, no process exists for that yet.
		if(this.paymentValue >= this.verificationThreshold) {
			console.log('Amount over verification threshold, BACS debit no longer a payment method.');
			delete repeatDonationPaymentMethods['payment-element'];
		}

		// Only show direct debits if Stripe's Payment Element loaded
		if(!this.bacsDebitPaymentDetailsCollection?.mounted) {
			console.log('Stripe Payment Element not mounted, BACS debit no longer a payment method.');
			delete repeatDonationPaymentMethods['payment-element'];
		}
		
		return repeatDonationPaymentMethods;
	};

	this.hideAllPaymentMethods = ()  => {
		const allPaymentMethods = {
			...this.repeatDonationPaymentMethods,
			...this.singleDonationPaymentMethods
		};
		console.log('Donate.hideAllPaymentMethods', allPaymentMethods);

		Object.values(allPaymentMethods).forEach(container => {
			container.hide();
		});
	};

	this.showPaymentMethods = (paymentMethodToContainerMap) => {
		console.log('Donate.showPaymentMethods', paymentMethodToContainerMap);
		Object.values(paymentMethodToContainerMap).forEach(container => {
			container.show();
		});
	};

	// This method updates the payment method screen to show the payment methods that are available.
	this.showAvailablePaymentMethods = () => {
		console.log('Donate.showAvailablePaymentMethods');

		// First, hide everything, it makes it simpler when swapping between repeat/single and
		// when changing the donation amount as we don't need to know what's visible, we can
		// just show what needs to be shown.
		this.hideAllPaymentMethods();

		const availablePaymentMethods = this.getAvailablePaymentMethods();
		this.showPaymentMethods(availablePaymentMethods);
	};

	this.init();
}